/**
 * Delay after the `load` event before the callback may run, on browsers with no
 * `requestIdleCallback` (Safari shipped it in 16.4).
 */
export const IDLE_FALLBACK_DELAY_MS = 2_000;

/**
 * Hard ceiling passed to `requestIdleCallback`. A main thread that never goes
 * idle must still end up running the callback.
 */
export const IDLE_TIMEOUT_MS = 5_000;

/**
 * Runs `run` once the page has finished loading AND the main thread is idle.
 * Returns a canceller.
 *
 * Measured on production 2026-08-21, real guest browser, `/id/jobs/<a job>`:
 * Grow pulled **32 requests spanning 13.1s → 58.3s** after navigation start,
 * and the job page's apply CTA — the thing a visitor is there to click, and
 * the thing the hourly synthetic revenue-path journey asserts — did not render
 * until **~49s**. The journey allows 30s, which is why it had been red 100
 * consecutive runs. `defer` was already set; `defer` only orders the script
 * against parsing, it does nothing about a widget bundle that then fetches
 * dozens of chunks while the app is still hydrating and firing its own API
 * calls.
 *
 * Grow is a measurement/monetization widget: nothing on the page depends on
 * it, and no revenue path runs through it. Yielding the main thread to the
 * app's own first render costs Grow a couple of seconds of session time and
 * costs the visitor nothing.
 */
export function scheduleWhenIdle(run: () => void): () => void {
	let cancelled = false;
	let cancel: () => void = () => {};

	const startWhenIdle = () => {
		if (cancelled) return;
		const w = window as Window & {
			requestIdleCallback?: (
				cb: () => void,
				opts?: { timeout: number },
			) => number;
			cancelIdleCallback?: (id: number) => void;
		};
		if (typeof w.requestIdleCallback === "function") {
			const id = w.requestIdleCallback(
				() => {
					if (!cancelled) run();
				},
				{ timeout: IDLE_TIMEOUT_MS },
			);
			cancel = () => w.cancelIdleCallback?.(id);
			return;
		}
		const id = window.setTimeout(() => {
			if (!cancelled) run();
		}, IDLE_FALLBACK_DELAY_MS);
		cancel = () => window.clearTimeout(id);
	};

	if (document.readyState === "complete") {
		startWhenIdle();
	} else {
		// `once` still needs the explicit removal below: unmounting before the
		// page finishes loading must not leave a listener behind that loads a
		// tracker into a page the component is no longer on.
		const onLoad = () => startWhenIdle();
		window.addEventListener("load", onLoad, { once: true });
		cancel = () => window.removeEventListener("load", onLoad);
	}

	return () => {
		cancelled = true;
		cancel();
	};
}
